import React, { useEffect, useState } from "react";
import { BetaForm as useBetaForm } from "@reusejs/react";
import { DateTime } from "luxon";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import {
  setBuyerAccessType,
  setBuyerLoginDate,
  setBuyerTokens,
  setBuyerUser,
} from "store";
import { Button } from "../../../shadcn/components/ui/button";
import { Input } from "../../../shadcn/components/ui/input";
import { Label } from "../../../shadcn/components/ui/label";
import Logo from "../../../icons/Logo";
import SellerLoginHero from "../../seller/Auth/SellerLoginHero";
import OtpInput from "../../seller/Auth/OtpInput";
import SpinLoader from "../SpinLoader";
import {
  getUser,
  intiateLoginWithOtp as initiateOtpLogin,
  loginWithOtp,
} from "services";

export default function Login() {
  const router = useRouter();
  const dispatch = useDispatch();
  const { accessToken } = useSelector((state: any) => state.buyerAuth);
  const [isRouteGuardLoading, setIsRouteGuardLoading] = useState(true);
  const [step, setStep] = useState<"email" | "otp">("email");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const form = useBetaForm({
    email: "",
    otp: "",
  });
  const isEmailStep = step === "email";

  const formConstraints = {
    email: {
      presence: {
        allowEmpty: false,
        message: "^Email is required.",
      },
      email: {
        message: "^Please enter a valid email address.",
      },
    },
    otp: {
      presence: {
        allowEmpty: false,
        message: "^OTP is required.",
      },
      length: {
        is: 6,
        message: "^OTP must be 6 digits.",
      },
      format: {
        pattern: "^[0-9]{6}$",
        flags: "i",
        message: "^OTP must contain only digits.",
      },
    },
  };

  useEffect(() => {
    if (!router.isReady) return;

    if (accessToken) {
      router.replace("/");
      return;
    }

    setIsRouteGuardLoading(false);
  }, [accessToken, router, router.isReady]);

  useEffect(() => {
    if (step === "email") {
      form.setValidationRules({ email: formConstraints.email });
    } else {
      form.setValidationRules({ otp: formConstraints.otp });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (step !== "otp" || resendCooldownSeconds <= 0) {
      return;
    }
    const timer = setInterval(() => {
      setResendCooldownSeconds((previousSeconds) => {
        if (previousSeconds <= 1) {
          clearInterval(timer);
          return 0;
        }
        return previousSeconds - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [step, resendCooldownSeconds]);

  const handleEmailSubmission = async () => {
    if (!form.validate()) {
      return;
    }
    try {
      setIsSendingOtp(true);
      const normalizedEmail = String(form.getField("email") || "")
        .trim()
        .toLowerCase();
      form.setField("email", normalizedEmail);
      await initiateOtpLogin(form);
      form.setField("otp", "");
      setResendCooldownSeconds(30);
      setStep("otp");
    } catch (error) {
      // Field-level errors are already set by service for known cases.
      console.log("Error while initiating OTP login", error);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (isResendingOtp || resendCooldownSeconds > 0) {
      return;
    }
    try {
      setIsResendingOtp(true);
      const normalizedEmail = String(form.getField("email") || "")
        .trim()
        .toLowerCase();
      form.setField("email", normalizedEmail);
      await initiateOtpLogin(form); // Resend OTP
      setResendCooldownSeconds(30);
    } catch (error) {
      console.log("Error in resend code", error);
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!form.validate()) {
      return;
    }
    try {
      setIsVerifyingOtp(true);
      const data: any = await loginWithOtp(form);
      dispatch(setBuyerTokens(data));
      const user: any = await getUser();
      console.log("user: ", user);
      dispatch(setBuyerUser(user));
      dispatch(
        setBuyerAccessType({
          access_type: "buyer",
        }),
      );
      const loginDateIST = DateTime.now()
        .setZone("Asia/Kolkata")
        .toFormat("yyyy-MM-dd");
      dispatch(setBuyerLoginDate({ loginDate: loginDateIST }));
      await router.replace("/");
    } catch (error: any) {
      console.log("check here error", error);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  if (isRouteGuardLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#F8F8F8]">
        <SpinLoader />
      </div>
    );
  }

  return (
    <div className="w-full dark:bg-[#152e4d]">
      <div className="w-full min-h-screen flex flex-col 2xl:w-[1340px] 2xl:mx-auto 2xl:px-0">
        <header className="w-full flex items-center justify-between px-customPaddingMobile lg:px-customPadding py-3 md:py-4">
          <Logo />
          <span className="text-lg font-medium capitalize tracking-wider text-brand-orange">
            Buyer App
          </span>
        </header>

        <main className="flex-1 flex items-center justify-center px-customPaddingMobile lg:px-customPadding">
          <div className="w-full flex flex-col justify-center items-center lg:items-stretch md:flex-row px-0 md:px-4 gap-6 md:gap-8">
            <SellerLoginHero />

            <div className="w-full lg:w-1/2 flex justify-center">
              <div className="w-full bg-[#F8F8F8] px-4 py-8 lg:py-10 lg:px-10 clip-path-bottomRightFooterMobile lg:clip-path-bottomRightPropertyCountCard rounded-xl shadow-sm">
                {isEmailStep ? (
                  <div className="pb-12">
                    <h2 className="text-2xl lg:text-3xl text-brand-textBlack">
                      Get Started
                    </h2>
                    <p className="text-xs lg:text-sm mt-2 text-gray-600">
                      Enter your email to access your account
                    </p>
                    <div className="mt-6 space-y-3">
                      <Label
                        htmlFor="buyer-login-email"
                        className="block text-xs lg:text-sm text-gray-700 mb-1"
                      >
                        Email address
                      </Label>
                      <Input
                        id="buyer-login-email"
                        type="email"
                        placeholder="Enter your email"
                        value={form.getField("email")}
                        onChange={(e) => form.setField("email", e.target.value)}
                        autoComplete="email"
                        className="w-full h-10 lg:h-12 px-3 lg:px-4 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-orange font-openSans text-sm"
                      />
                      {form.errors.get("email") && (
                        <p className="text-xs text-red-700">
                          {form.errors.get("email")[0]}
                        </p>
                      )}
                    </div>
                    <Button
                      className="w-full mt-6 lg:mt-10"
                      onClick={handleEmailSubmission}
                      disabled={isSendingOtp}
                    >
                      {isSendingOtp ? "Sending OTP..." : "Get OTP"}
                    </Button>
                    <p className="mt-4 text-[11px] lg:text-xs text-gray-500 text-center leading-snug">
                      By continuing, you agree to the{" "}
                      <span className="underline">terms &amp; conditions</span>{" "}
                      and <span className="underline">privacy policy</span>.
                    </p>
                  </div>
                ) : (
                  <div className="pb-12">
                    <h2 className="text-2xl lg:text-3xl text-brand-textBlack">
                      Verify OTP
                    </h2>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-3">
                      <p className="text-xs lg:text-sm text-gray-600">
                        OTP sent to{" "}
                        <span className="font-semibold">
                          {form.getField("email") || "your email"}
                        </span>
                      </p>
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-xs lg:text-sm underline text-login-color self-start"
                        onClick={() => {
                          setStep("email");
                        }}
                      >
                        Change
                      </Button>
                    </div>
                    <div className="mt-6">
                      <p className="text-xs lg:text-sm font-medium mb-3">
                        Enter 6-digit OTP
                      </p>
                      <OtpInput
                        otp={form.getField("otp")}
                        onChange={(value) => form.setField("otp", value)}
                      />
                      {form.errors.get("otp") && (
                        <p className="mt-2 text-xs text-red-700">
                          {form.errors.get("otp")[0]}
                        </p>
                      )}
                      <p className="mt-3 text-[11px] lg:text-xs text-gray-500">
                        Didn&apos;t receive the OTP?{" "}
                        <Button
                          type="button"
                          variant="link"
                          className="h-auto p-0 underline text-login-color text-[11px] lg:text-xs"
                          onClick={handleResendOtp}
                          disabled={isResendingOtp || resendCooldownSeconds > 0}
                        >
                          {isResendingOtp
                            ? "Resending..."
                            : resendCooldownSeconds > 0
                            ? `Resend OTP in ${resendCooldownSeconds}s`
                            : "Resend OTP"}
                        </Button>
                      </p>
                    </div>
                    <Button
                      className="w-full mt-8 lg:mt-6"
                      onClick={handleVerifyOtp}
                      disabled={isVerifyingOtp}
                    >
                      {isVerifyingOtp ? "Verifying..." : "Verify"}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
